import { createContext, ReactNode, useContext } from "react";

import { UserProps } from "../types/admin";
import { AllChatProps, TodoGroupProps } from "../types/chat";
import {
    ChatNoteMetaProps,
    MyNoteMetaProps,
    SharedNoteMetaProps,
    TaskNoteMetaProps,
    TeamNoteMetaProps,
} from "../types/notes";
import { ProjectProps, SearchTeamTasksResponse, TaskTableProps } from "../types/tasks";

// One note row for the "#" mention menu, tagged with its note kind so the
// chip can rebuild the correct deep-link URL (the four note kinds use
// different URL shapes — see `entityRefToHref`). The metadata is exactly
// the shape `useNM` already holds; the provider just stamps `kind` while
// merging the four arrays so the menu builder doesn't re-derive it.
export type HashNoteEntry =
    | ({ kind: "my" } & MyNoteMetaProps)
    | ({ kind: "task" } & TaskNoteMetaProps)
    | ({ kind: "chat" } & ChatNoteMetaProps)
    | ({ kind: "shared" } & SharedNoteMetaProps)
    // Team Notes. Backed by note_type 1 like "my" and "shared", but
    // kept a distinct kind so the deep-link URL resolves to the Team
    // Notes section rather than My Notes.
    | ({ kind: "team" } & TeamNoteMetaProps);

// The data backing the "#" mention suggestion menu. Provided once at the
// App root (where the task / note / chat / project hooks already live) and
// read by every editor's `HashSuggestionMenuController` via context —
// mirrors how `MentionGroupsContext` feeds the `@group` menu, avoiding a
// four-list prop-drill through a dozen editors.
export type HashMentionData = {
    // The OPEN project's task rows, in full table shape. Instant and
    // always current (optimistic creates land here first), but one
    // project only — `teamTasks` is what makes the menu team-wide.
    tasks: TaskTableProps[];
    // Every active task in the team, across all projects, from
    // `/search/teamTasks/` — the same list the task search bars use.
    // Fetched lazily on the first `#` query (see `refresh`) rather than at
    // boot, since most sessions never open the menu. The `#` menu merges
    // it with `tasks` above, deduped by project+task id.
    teamTasks: SearchTeamTasksResponse[];
    notes: HashNoteEntry[];
    // Already filtered to GM (chatType === 2) by the provider. The
    // editors' "#" menu keeps this GM-only scope deliberately (notes
    // embed in shared surfaces where a DM title would leak who you
    // talk to); the agent-input mention picker uses `allChats` below.
    chats: AllChatProps[];
    // The unfiltered chat list (DM / GM / PM / MDM) for the agent-input
    // "#" mention picker — agent asks are private to the requester, so
    // the leak rationale above doesn't apply there.
    allChats: AllChatProps[];
    projects: ProjectProps[];
    // Daily todo groups (agent-input "#" picker only — todos are the
    // requester's own, so the editors' shared-surface leak rationale
    // rules them out of the BlockNote menu the same way DMs are).
    todoGroups: TodoGroupProps[];
    // Current user — used by the task-mention hover card to fetch a task's
    // live status (`loadSpecificTask` needs `myself.teamId`). Null outside
    // the provider; the hover card degrades to a static label.
    myself: UserProps | null;
    /**
     * Re-pull the lists above from the server. Note metadata and the
     * project list are otherwise fetched ONCE per page load, so anything
     * created elsewhere (a teammate's note, another tab, another device)
     * stayed missing from the `#` menu until a reload — the staleness
     * this exists to fix.
     *
     * Fire-and-forget and throttled app-wide (see `hashMentionRefresh`),
     * so hot call sites — one per `#` keystroke, per window focus, per
     * mounted editor — collapse to at most one round of fetches.
     */
    refresh: () => void;
};

const EMPTY: HashMentionData = {
    tasks: [],
    teamTasks: [],
    notes: [],
    chats: [],
    allChats: [],
    projects: [],
    todoGroups: [],
    myself: null,
    refresh: () => {},
};

const HashMentionDataContext = createContext<HashMentionData | null>(null);

export const HashMentionDataProvider = ({
    value,
    children,
}: {
    value: HashMentionData;
    children: ReactNode;
}) => <HashMentionDataContext.Provider value={value}>{children}</HashMentionDataContext.Provider>;

// Returns an empty dataset when used outside the provider (e.g. editors
// mounted on pre-auth pages) so the menu just shows nothing instead of
// crashing.
// eslint-disable-next-line react-refresh/only-export-components
export const useHashMentionData = (): HashMentionData => {
    return useContext(HashMentionDataContext) ?? EMPTY;
};
